import { Loaded } from "models/loaded";
import { Maybe } from "models/maybe";
import { useCallback, useEffect, useMemo, useState } from "react";

export type Setter<T extends object | string | number | null> = (
  update: T | ((current: T) => T)
) => void;

export interface SyncedState<T extends object | null> {
  client: Loaded<T>;
  setClient: Setter<T>;
  server: Loaded<T>;
  setServer: Setter<T>;
}

interface InternalSyncedState<T> {
  /**
   * Most recent value provided by the user.
   *
   * This will be invalid immediately after the synced state has been
   * instantiated, until the `source` function has returned successfully.
   */
  client: Maybe<T>;

  /** Most recent value acknowledged by the server. */
  server: Maybe<T>;

  /**
   * Client value the server last acknowledged. An equivalent client value is
   * not sent again, because the server's reply may never compare equal to it.
   */
  acknowledgedClient: Maybe<T>;

  /** Whether the server is currently being updated to match the client. */
  syncing: boolean;

  /** Whether or not an error prevented the most recent value from syncing. */
  error: boolean;
}

/**
 * Maintains optimistic client state and the latest server-acknowledged state.
 * Serialises updates and skips a client value equivalent to the server value
 * or to the client value the server last acknowledged. A changed source resets
 * the state before loading the replacement value. The hook detects source
 * changes by function identity. Callers must memoise the source function.
 */
export const useSyncedState = <T extends object | null>(
  source: (() => Promise<T>) | null,
  update: (client: T, server: T) => Promise<T>,
  compare: (left: T, right: T) => boolean
): SyncedState<T> => {
  const [state, setState] = useState<InternalSyncedState<T>>({
    client: { present: false },
    server: { present: false },
    acknowledgedClient: { present: false },
    syncing: false,
    error: false,
  });

  useEffect(() => {
    if (source) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets state before async source fetch
      setState({
        client: { present: false },
        server: { present: false },
        acknowledgedClient: { present: false },
        syncing: true,
        error: false,
      });
      source()
        .then((server) => {
          setState({
            client: { present: true, value: server },
            server: { present: true, value: server },
            acknowledgedClient: { present: true, value: server },
            syncing: false,
            error: false,
          });
        })
        .catch(() => {
          setState({
            client: { present: false },
            server: { present: false },
            acknowledgedClient: { present: false },
            syncing: false,
            error: true,
          });
        });
    } else {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets state when source is null
      setState({
        client: { present: false },
        server: { present: false },
        acknowledgedClient: { present: false },
        syncing: false,
        error: false,
      });
    }
  }, [source]);

  const setClient = useCallback((update: T | ((current: T) => T)) => {
    setState((s) => {
      // Ignore edits until the source value is loaded.
      if (!s.client.present) {
        return s;
      }
      return {
        ...s,
        client: {
          present: true,
          value:
            update == null || typeof update === "object"
              ? update
              : update(s.client.value),
        },
        error: false,
      };
    });
  }, []);

  useEffect(() => {
    if (
      !state.syncing &&
      !state.error &&
      state.client.present &&
      state.server.present &&
      !(
        state.acknowledgedClient.present &&
        compare(state.client.value, state.acknowledgedClient.value)
      ) &&
      !compare(state.client.value, state.server.value)
    ) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- marks syncing before dispatching async update
      setState((s) => ({ ...s, syncing: true, error: false }));

      const sentClient = state.client.value;
      update(sentClient, state.server.value)
        .then((server) => {
          setState((s) => ({
            client: s.client,
            server: { present: true, value: server },
            acknowledgedClient: { present: true, value: sentClient },
            syncing: false,
            error: false,
          }));
        })
        .catch(() => {
          setState((s) => ({ ...s, syncing: false, error: true }));
        });
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- state.server, compare, update deliberately excluded to prevent spurious syncs when server acknowledges
  }, [state.client, state.syncing, state.error]);

  const setServer = useCallback((update: T | ((current: T) => T)) => {
    setState((s) => ({
      ...s,
      server:
        update == null || typeof update === "object"
          ? { present: true, value: update }
          : s.server.present
            ? { present: true, value: update(s.server.value) }
            : { present: false },
    }));
  }, []);

  return useMemo(
    () => ({
      client: state.client.present
        ? { status: "success", data: state.client.value, fetching: false }
        : state.error
          ? { status: "error" }
          : state.syncing
            ? { status: "loading" }
            : { status: "waiting" },
      setClient,
      server: state.syncing
        ? { status: "loading" }
        : state.error
          ? { status: "error" }
          : state.server.present
            ? { status: "success", data: state.server.value, fetching: false }
            : { status: "waiting" },
      setServer,
    }),
    [state, setClient, setServer]
  );
};

export const unpackClientValue = <T extends object | null>(
  syncedState: SyncedState<T>
): T | undefined => {
  return syncedState.client.status === "success"
    ? syncedState.client.data
    : undefined;
};

export const unpackServerValue = <T extends Exclude<object, null>>(
  syncedState: SyncedState<T>
): T | undefined => {
  return syncedState.server.status === "success"
    ? syncedState.server.data
    : undefined;
};
